import { ptBR } from "date-fns/locale";
import { formatInTimeZone, toDate } from "date-fns-tz";

/**
 * Formatação de exibição de data/hora.
 * Tudo o que passa por `parseBackendDateTimeParts` é mostrado no fuso **America/Sao_Paulo**,
 * independentemente do fuso do browser.
 *
 * - Instantâneo com `Z` ou offset ISO → converte para o relógio de São Paulo.
 * - `YYYY-MM-DD` / `YYYY-MM-DD HH:mm:ss` sem fuso → interpretado como **data/hora civil em São Paulo**.
 */

/** Fuso fixo para exibição (não usar fuso do browser). */
export const DISPLAY_TIME_ZONE = "America/Sao_Paulo";

/** Data inválida ou ausente — só data */
export const EMPTY_DATE = "--/--/----";

/** Só hora */
export const EMPTY_TIME = "--:--";

/** Data e hora */
export const EMPTY_DATETIME = "--/--/---- --:--";

function isNil(value) {
  return value == null || value === "";
}

/**
 * ISO com `Z` ou offset (`+03:00`) = instante absoluto.
 */
function hasExplicitTimeZoneInString(s) {
  const t = String(s).trim();
  if (/Z$/i.test(t)) return true;
  // +03:00, +0300, -03:00 no fim (após segundos ou ms)
  return /[+-]\d{2}:\d{2}$/.test(t) || /[+-]\d{4}$/.test(t);
}

/** `YYYY-MM-DD` ou `YYYY-MM-DD HH:mm:ss` / `T` sem sufixo de fuso → horário civil em São Paulo. */
function isNaiveSqlOrIsoDateTime(s) {
  const t = String(s).trim();
  if (hasExplicitTimeZoneInString(t)) return false;
  return /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)?$/.test(t);
}

function partsFromInstantInSaoPaulo(d) {
  if (!d || Number.isNaN(d.getTime())) return null;
  return {
    year: formatInTimeZone(d, DISPLAY_TIME_ZONE, "yyyy"),
    month: formatInTimeZone(d, DISPLAY_TIME_ZONE, "MM"),
    day: formatInTimeZone(d, DISPLAY_TIME_ZONE, "dd"),
    hours: formatInTimeZone(d, DISPLAY_TIME_ZONE, "HH"),
    minutes: formatInTimeZone(d, DISPLAY_TIME_ZONE, "mm"),
    seconds: formatInTimeZone(d, DISPLAY_TIME_ZONE, "ss"),
  };
}

/**
 * Partes para exibição sempre no fuso `DISPLAY_TIME_ZONE` (America/Sao_Paulo).
 */
export function parseBackendDateTimeParts(input) {
  if (isNil(input)) return null;

  if (input instanceof Date) {
    return partsFromInstantInSaoPaulo(input);
  }

  const raw = String(input).trim();

  if (hasExplicitTimeZoneInString(raw)) {
    const d = new Date(raw);
    return partsFromInstantInSaoPaulo(d);
  }

  if (isNaiveSqlOrIsoDateTime(raw)) {
    const normalized = raw.replace(" ", "T");
    const d = toDate(normalized, { timeZone: DISPLAY_TIME_ZONE });
    return partsFromInstantInSaoPaulo(d);
  }

  const d = new Date(raw);
  return partsFromInstantInSaoPaulo(d);
}

/**
 * Instantâneo em ms para ordenação cronológica (mesma interpretação de string/`Date` que `parseBackendDateTimeParts`).
 */
export function toTimestampForSort(input) {
  if (isNil(input)) return 0;
  if (input instanceof Date) {
    const t = input.getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  const raw = String(input).trim();
  if (hasExplicitTimeZoneInString(raw)) {
    const d = new Date(raw);
    const t = d.getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  if (isNaiveSqlOrIsoDateTime(raw)) {
    const normalized = raw.replace(" ", "T");
    const d = toDate(normalized, { timeZone: DISPLAY_TIME_ZONE });
    const t = d.getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  const d = new Date(raw);
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Exibição: `dd/MM/yyyy` */
export function formatDate(value) {
  const p = parseBackendDateTimeParts(value);
  if (!p) return EMPTY_DATE;
  return `${p.day}/${p.month}/${p.year}`;
}

/** Exibição: `HH:mm` */
export function formatTime(value) {
  const p = parseBackendDateTimeParts(value);
  if (!p) return EMPTY_TIME;
  return `${p.hours}:${p.minutes}`;
}

/** Exibição: `dd/MM/yyyy HH:mm` */
export function formatDateTime(value) {
  const p = parseBackendDateTimeParts(value);
  if (!p) return EMPTY_DATETIME;
  return `${p.day}/${p.month}/${p.year} ${p.hours}:${p.minutes}`;
}

/** Exibição: `dd/MM/yyyy HH:mm:ss` */
export function formatDateTimeSeconds(value) {
  const p = parseBackendDateTimeParts(value);
  if (!p) return EMPTY_DATETIME;
  return `${p.day}/${p.month}/${p.year} ${p.hours}:${p.minutes}:${p.seconds}`;
}

/**
 * Dia civil local no formato `yyyy-MM-dd` (filtros que trabalham com `Date` do picker).
 */
export function formatCalendarDay(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Converte valor do DatePicker Ant Design v4 (moment) ou `Date`/string parseável em `Date`.
 */
export function pickerValueToDate(value) {
  if (value == null || value === "") return undefined;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d;
}

/** Nome de ficheiro: `yyyy_MM_dd_HH_mm_ss` no fuso de São Paulo. */
export function formatNowForFilename() {
  return formatInTimeZone(new Date(), DISPLAY_TIME_ZONE, "yyyy_MM_dd_HH_mm_ss");
}

/** Nome de export: `dd-MM-yyyy-HH-mm` no fuso de São Paulo. */
export function formatNowForExportStamp() {
  return formatInTimeZone(new Date(), DISPLAY_TIME_ZONE, "dd-MM-yyyy-HH-mm");
}

/** Mês abreviado em PT (ex.: `JAN`), relógio civil em São Paulo. */
export function formatMonthAbbrevPtUpper(input) {
  let p = parseBackendDateTimeParts(input);
  if (!p && input != null) {
    const t = String(input).trim();
    const br = t.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (br) {
      p = { day: br[1], month: br[2], year: br[3] };
    }
  }
  if (!p) return "";
  const d = new Date(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    12,
    0,
    0,
    0
  );
  if (Number.isNaN(d.getTime())) return "";
  return formatInTimeZone(d, DISPLAY_TIME_ZONE, "LLL", { locale: ptBR })
    .replace(/\./g, "")
    .toUpperCase();
}

/**
 * Legado timeline: `dd/MM/yyyy (HH:mm)` (sempre relógio de São Paulo)
 */
export function formatSqlDateTimeParen(dateString) {
  if (isNil(dateString)) return EMPTY_DATETIME;
  const p = parseBackendDateTimeParts(String(dateString).trim());
  if (!p) return EMPTY_DATETIME;
  return `${p.day}/${p.month}/${p.year} (${p.hours}:${p.minutes})`;
}

/**
 * Apenas `yyyy-MM-dd` → `dd/MM/yyyy` (ex.: vencimento sem hora).
 * Não usar para timestamps completos.
 */
export function formatIsoDateOnlyToBr(isoDateOnly) {
  if (isNil(isoDateOnly)) return EMPTY_DATE;
  const head = String(isoDateOnly).trim().split(/\s/)[0];
  const p = head.split("-");
  if (p.length !== 3) return EMPTY_DATE;
  const [y, m, d] = p;
  if (!y || !m || !d) return EMPTY_DATE;
  return `${d}/${m}/${y}`;
}

/**
 * Normaliza valor de formulário para envio à API (`Date` ou string parseável).
 */
export function toIsoString(value) {
  if (value == null) return undefined;
  if (value instanceof Date) return value.toISOString();
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/**
 * Parâmetro de filtro dia civil (`yyyy-MM-dd`) para Horizon LocalDate
 */
export function toCalendarDayParam(value) {
  if (value == null || value === "") return undefined;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const head = s.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return formatCalendarDay(d);
}

/**
 * Horizon envia relógio civil BR com sufixo `+0000`, `+00:00` ou `Z` (não é UTC real).
 */
export function normalizeHorizonProcessorDateTime(value) {
  if (isNil(value)) return value;
  return String(value)
    .trim()
    .replace(/[+-]\d{2}:\d{2}$/i, "")
    .replace(/[+-]\d{4}$/i, "")
    .replace(/Z$/i, "");
}

/** Exibição de `dataProcessamento` e campos equivalentes da Horizon. */
export function formatHorizonDateTime(value) {
  return formatDateTime(normalizeHorizonProcessorDateTime(value));
}

/** Horizon: `dd/MM/yyyy - HH:mm` (lista de ocorrências na timeline). */
export function formatHorizonDateTimeDash(value) {
  const formatted = formatHorizonDateTime(value);
  if (formatted === EMPTY_DATETIME) return EMPTY_DATETIME;
  const spaceIdx = formatted.lastIndexOf(" ");
  if (spaceIdx === -1) return formatted;
  return `${formatted.slice(0, spaceIdx)} - ${formatted.slice(spaceIdx + 1)}`;
}

/** Aliases para migração gradual */
export const getFormatData = formatDate;
export const getFormatCalendar = formatCalendarDay;
export const getFormatDateAndTimeNoTz = formatDateTime;
export const getFormatDateTime = formatSqlDateTimeParen;
export const getFormatDateReverse = formatIsoDateOnlyToBr;
